import React, { ReactElement, ReactNode } from 'react';
import {
  alpha,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  StyledEngineProvider,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Button, Typography } from '../index';
import * as CommonIcons from 'assets/common';
import { classNames, getDataPropsFromRest } from 'utilities/Utils';

export type SubClassProps = {
  zIndexValue: string;
  isConfirm: boolean;
  showLightSubtitleColor?: boolean;
  titleFontSize?: string;
  customZIndexValue?: string | number;
};

// Type defination for Modal.tsx
export type ModalProps = {
  children: React.ReactNode;
  onCrossCancel?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  onClose?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  onConfirm?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  variant?: 'confirm' | 'info' | 'filterable';
  size?: 'small' | 'medium' | 'large' | 'very-large';
  title?: string | React.ReactNode;
  subTitle?: string | React.ReactNode;
  contentClass?: string;
  negativeText?: string;
  positiveText?: string;
  confirmDisabled?: boolean;
  hideFooter?: boolean;
  widthAdjust?: boolean;
  increaseZIndex?: boolean;
  className?: string;
  showFooterDivider?: boolean;
  disableBackdropClick?: boolean;
  removeCrossBtn?: boolean;
  showLightSubtitleColor?: boolean;
  negativeBtnDisabled?: boolean;
  classNamePositiveText?: string;
  classNameNegativeText?: string;
  toLowerCase?: boolean;
  headerClass?: string;
  footerClass?: string;
  titleFontSize?: string;
  titleClass?: string;
  removeCancelButton?: boolean;
  subTitleClass?: string;
  customZIndexValue?: string | number;
  headerElement?: React.ReactNode;
  overrideCta?: (ctaEl: ReactElement) => ReactElement;
  errorMsg?: ReactNode;
};

// End of type defination for Modal.tsx

const useStyles = makeStyles(theme => ({
  root: (props: SubClassProps) => ({
    zIndex: props.zIndexValue,
    '& .MuiDialog-paperWidthSm': {
      maxWidth: 'fit-content',
    },

    '& .sr-modal-content-latest-core': {
      minWidth: '320px',
      maxWidth: '500px',
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'auto',
      overflowX: 'auto',
    },
    '& .sr-modal-content-latest-core.small': {
      width: '400px',
    },
    '& .sr-modal-content-latest-core.large': {
      width: '650px',
    },
    '& .sr-modal-content-latest-core.very-large': {
      maxWidth: 'fit-content',
    },
    '& .MuiDialogContent-root': {
      paddingTop: '8px',
      paddingBottom: '8px',
    },
  }),
  zIndex: (props: SubClassProps) => ({
    zIndex: props.customZIndexValue
      ? props.customZIndexValue
      : '100000001 !important',
  }),
  contentClass: {
    padding: '16px !important',
  },
  crossContainer: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    '& svg': {
      height: '24px',
      width: '24px',
    },
  },
  headerContainer: (props: SubClassProps) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    '& > div': {
      color: '#404040',
      fontSize: props.titleFontSize || '18px',
    },
  }),
  subTitle: (props: SubClassProps) => ({
    fontSize: '12px',
    fontWeight: 'normal',
    display: 'block',
    color: props.showLightSubtitleColor && theme.palette.text.secondary,
  }),
  footer: {
    justifyContent: 'flex-end',
    alignItems: 'unset',
    padding: '10px',
    whiteSpace: 'pre',
    '& button': {
      borderRadius: '4px !important',
      height: '40px !important',
      textTransform: 'capitalize',
    },
  },
  filterableHeader: {
    backgroundColor: alpha(theme.palette.primary.main, 0.15),
    padding: '24px 30px',
    margin: '-16px',
  },
  divider: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  backdrop: {
    backgroundColor: 'rgba(49, 49, 49, 0.5)',
  },
  headerroot: {
    padding: '16px',
    color: 'blue',
  },
  filterableTitle: {
    color: alpha(theme.palette.grey[900], 0.7),
    fontSize: '21px',
    fontWeight: 500,
    lineHeight: 1.71,
    margin: '0 0 14px',
  },
}));

const sizeClassMap = {
  small: 'sr-modal-content-latest-core small',
  medium: 'sr-modal-content-latest-core',
  large: 'sr-modal-content-latest-core large',
  'very-large': 'sr-modal-content-latest-core very-large',
};

export default function Modal(props: ModalProps) {
  const {
    children,
    onCrossCancel,
    onClose,
    onConfirm,
    variant = 'confirm',
    size = 'medium',
    title,
    subTitle,
    negativeText = 'Cancel',
    positiveText = 'Confirm',
    classNamePositiveText = '',
    classNameNegativeText = '',
    confirmDisabled = false,
    hideFooter = false,
    increaseZIndex = false,
    className = '',
    showFooterDivider = false,
    disableBackdropClick = true,
    removeCrossBtn = false,
    contentClass = '',
    showLightSubtitleColor = false,
    negativeBtnDisabled = false,
    toLowerCase = true,
    headerClass = '',
    footerClass = '',
    subTitleClass = '',
    titleFontSize = '18px',
    titleClass = '',
    removeCancelButton = false,
    customZIndexValue = '',
    headerElement = null,
    overrideCta = e => e,
    errorMsg = '',
    ...rest
  } = props;
  const zIndexValue = increaseZIndex ? '100000000000000000 !important' : 'auto';

  const dataTestIdProps = getDataPropsFromRest(rest);

  const classes = useStyles({
    zIndexValue,
    isConfirm: variant === 'confirm',
    showLightSubtitleColor,
    customZIndexValue,
    titleFontSize,
  });

  const isInfo = variant === 'info';

  const negativeLabel = negativeText ?? (isInfo ? 'Cancel' : 'Close');
  const negativeBtnVariant = isInfo ? 'contained' : 'text';
  const containerWrapperClass = sizeClassMap[size];

  const renderCloseIcon = () => {
    return (
      !removeCrossBtn && (
        <div
          onClick={onCrossCancel ?? onClose}
          className={classes.crossContainer}
        >
          <CommonIcons.Close />
        </div>
      )
    );
  };

  let header = null;
  if (variant === 'filterable') {
    header = (
      <div className={classes.filterableHeader}>
        <h1 className={classNames(classes.filterableTitle, titleClass)}>
          {title}
        </h1>
        {headerElement}
      </div>
    );
  } else if (title) {
    header = (
      <DialogTitle
        className={classNames(classes.divider, headerClass)}
        classes={{ root: classes.headerroot }}
      >
        <div>
          <div className={classNames(classes.headerContainer)}>
            <div className={titleClass}>{title}</div>
            {renderCloseIcon()}
          </div>
          {subTitle && (
            <span
              className={`${
                showLightSubtitleColor ? '' : 'text-common-dark'
              }  ${classes.subTitle} ${subTitleClass}`}
            >
              {subTitle}
            </span>
          )}
        </div>
      </DialogTitle>
    );
  }

  return (
    <StyledEngineProvider injectFirst>
      <Dialog
        open={true}
        classes={{ root: classes.root }}
        className={classNames(className, classes.zIndex)}
        BackdropProps={{ classes: { root: classes.backdrop } }}
        sx={{
          '& .MuiDialog-paperWidthSm': {
            maxWidth: '768px',
          },
        }}
        {...(!disableBackdropClick ? { onClose } : {})}
      >
        <div className={containerWrapperClass} {...dataTestIdProps}>
          {header}
          <DialogContent
            className={contentClass ? contentClass : classes.contentClass}
          >
            {children}
          </DialogContent>
          {showFooterDivider && <div className={classes.divider}></div>}
          {!hideFooter && (
            <DialogActions className={classNames(classes.footer, footerClass)}>
              {errorMsg && (
                <Typography
                  themeColor="error.dark"
                  className="flex-1 self-center ml-2"
                >
                  {errorMsg}
                </Typography>
              )}
              {!removeCancelButton && (
                <Button
                  size="medium"
                  variant={negativeBtnVariant}
                  className={`${classNameNegativeText}`}
                  disabled={negativeBtnDisabled}
                  onClick={onClose}
                  data-test-id="modal-footer-negative-button"
                >
                  {negativeLabel}
                </Button>
              )}
              {!isInfo &&
                overrideCta(
                  <Button
                    toLowerCase={toLowerCase}
                    size="medium"
                    onClick={onConfirm}
                    disabled={confirmDisabled}
                    className={`marginL16 ${classNamePositiveText}`}
                    data-test-id="modal-footer-positive-button"
                  >
                    {positiveText}
                  </Button>,
                )}
            </DialogActions>
          )}
        </div>
      </Dialog>
    </StyledEngineProvider>
  );
}
